import { memo, useMemo } from 'react';
import { getAvatarUrl } from '@/lib/avatars';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Comment } from '@/hooks/useComments';
import { formatDistanceToNow } from 'date-fns';
import { cn } from '@/lib/utils';

interface CommentPreviewProps {
  comment: Comment;
  totalCount: number;
  onClick: () => void;
}

function CommentPreviewComponent({ comment, totalCount, onClick }: CommentPreviewProps) {
  const displayName = comment.profile?.display_name || 'Anonymous';
  const avatarUrl = getAvatarUrl(comment.profile?.avatar_url, comment.user_id);
  const initials = displayName.charAt(0).toUpperCase();
  
  const timeAgo = useMemo(() => 
    formatDistanceToNow(new Date(comment.created_at), { addSuffix: false }),
    [comment.created_at]
  );

  return (
    <button
      onClick={onClick}
      className={cn(
        "w-full text-left p-3 rounded-xl",
        "bg-muted/30 hover:bg-muted/50",
        "transition-colors duration-200",
        "group"
      )}
    >
      <div className="flex items-start gap-2.5">
        <Avatar className="h-6 w-6 flex-shrink-0">
          <AvatarImage src={avatarUrl || undefined} alt={displayName} />
          <AvatarFallback className="bg-muted" />
        </Avatar>
        
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5 text-xs">
            <span className="font-medium text-foreground">{displayName}</span>
            <span className="text-muted-foreground">·</span>
            <span className="text-muted-foreground">{timeAgo}</span>
          </div>
          
          <p className="mt-1 text-sm text-foreground/80 line-clamp-2">
            {comment.content}
          </p>
          
          {totalCount > 1 && (
            <p className="mt-2 text-xs font-medium text-primary group-hover:underline">
              View all {totalCount} comments
            </p>
          )}
        </div>
      </div>
    </button>
  );
}

export const CommentPreview = memo(CommentPreviewComponent);
